import { useMemo, useState } from "react";
import { Search, Sparkles, MessageCircle } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/salonData";
import { openWhatsApp, bookServiceMessage } from "@/lib/whatsapp";
import { Reveal } from "@/lib/useReveal";

export default function Services() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SERVICES.filter((s) => {
      const matchCat = category === "All" || s.category === category;
      const matchQ =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, category]);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 md:py-28 bg-gradient-to-b from-white via-[#fffafc] to-[#fff5f8]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="heading-flourish">Premium Service Menu</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1b18] font-medium mt-4 leading-tight">
            Beauty Services
            <span className="text-gradient-gold italic"> Crafted For You</span>
          </h2>
          <p className="mt-5 text-[#5c4642] text-base md:text-lg leading-relaxed">
            Search any service or filter by category. Every booking opens
            directly on WhatsApp for instant confirmation.
          </p>
        </Reveal>

        {/* Search */}
        <Reveal delay={150}>
          <div className="mt-10 max-w-2xl mx-auto">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-[#b56477]"
              />
              <input
                data-testid="services-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search facial, makeup, hair spa..."
                className="w-full pl-12 pr-5 py-4 rounded-full bg-white border border-[#d98a99]/25 shadow-sm focus:outline-none focus:border-[#d98a99] focus:ring-4 focus:ring-[#d98a99]/15 text-sm md:text-base"
              />
            </div>
          </div>
        </Reveal>

        {/* Category chips */}
        <Reveal delay={250}>
          <div className="mt-6 flex flex-wrap justify-center gap-2 md:gap-3">
            {SERVICE_CATEGORIES.map((cat) => {
              const active = category === cat;
              return (
                <button
                  key={cat}
                  data-testid={`filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 ${
                    active
                      ? "btn-rose shadow-md"
                      : "bg-white text-[#5c4642] border border-[#d98a99]/20 hover:border-[#d98a99] hover:text-[#b56477]"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Grid */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {filtered.length === 0 && (
            <div
              data-testid="services-no-results"
              className="col-span-full text-center py-16 text-[#5c4642]"
            >
              <Sparkles size={28} className="mx-auto text-[#d4af37] mb-3" />
              No services match "{query}". Try a different search.
            </div>
          )}
          {filtered.map((s) => (
            <div
              key={`${s.category}-${s.name}`}
              data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="service-card flex flex-col"
            >
              <div className="text-[10px] uppercase tracking-wider text-[#d4af37] font-semibold mb-1">
                {s.category}
              </div>
              <div className="font-display text-xl text-[#2a1b18] font-semibold leading-tight">
                {s.name}
              </div>
              <div className="mt-3 flex items-end gap-1 text-[#b56477]">
                <span className="text-2xl font-display font-medium">
                  ₹{s.price.toLocaleString("en-IN")}
                </span>
                {s.priceSuffix && (
                  <span className="text-xs text-[#5c4642] mb-1">
                    {s.priceSuffix}
                  </span>
                )}
              </div>
              <button
                onClick={() =>
                  openWhatsApp(bookServiceMessage(s.name, s.price))
                }
                data-testid={`book-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full text-sm font-medium btn-outline-rose"
              >
                <MessageCircle size={14} />
                Book Now
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
